import React, { useEffect, useMemo, useState } from "react";
import {
  PlusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  BarsArrowUpIcon,
  BarsArrowDownIcon,
  Cog6ToothIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import TableComponent from "../../components/TableComponent";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";

const JSON_HEADERS = { headers: { "Content-Type": "application/json" } };

const isSuperAdmin = () => String(localStorage.getItem("userId")) === "1";
const getUserPermissions = () => {
  try {
    const raw = localStorage.getItem("permissions");
    return raw ? JSON.parse(raw).map((p) => p.permissionSlug) : [];
  } catch {
    return [];
  }
};
const hasPermission = (slug) =>
  isSuperAdmin() || getUserPermissions().includes(slug);

const Input = (props) => (
  <input
    {...props}
    className={`input input-bordered w-full ${props.className || ""}`}
  />
);

const Modal = ({ isOpen, title, onClose, children, footer }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center">
      <div
        className="relative bg-white w-full max-w-3xl mx-2 rounded-2xl p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">{title}</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="space-y-4">{children}</div>
        {footer && <div className="mt-6">{footer}</div>}
      </div>
    </div>
  );
};

/* ===== Helpers cho auto category ===== */
const AUTO_UNITS = {
  activities: "Lần tham gia",
  referral: "Hội viên",
  trade_internal: "Giao dịch",
  trade_external: "Giao dịch",
};
const getAutoUnit = (type) => AUTO_UNITS[type] || "Mốc";

/** Parse label auto: ">= 3 Lần tham gia", "<=10 Hội viên", "5 Giao dịch" (= mặc định) */
const parseAutoLabel = (label = "") => {
  const trimmed = String(label || "").trim();
  const m = trimmed.match(/^(<=|>=|<|>|=)?\s*([\d]+(?:[.,]\d+)?)?/);
  const op = m && m[1] ? m[1] : "=";
  const valueStr = m && m[2] ? m[2].replace(",", ".") : "";
  const value = valueStr === "" ? "" : Number(valueStr);
  return { op, value };
};

/* ================= LevelEditorModal (không hiển thị order, nhưng có nút lên/xuống) ================= */
const LevelEditorModal = ({ open, onClose, category }) => {
  const canEdit = hasPermission("POINT_SETTINGS");
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);

  const isAuto = !!category?.autoType;
  const autoUnit = getAutoUnit(category?.type);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({
    id: null,
    // manual
    label: "",
    // auto
    op: "=",
    value: "",
    // common
    points: "",
    note: "",
  });

  const [confirm, setConfirm] = useState({ open: false, id: null });

  const loadLevels = async () => {
    if (!open || !category?.id) return;
    setLoading(true);
    try {
      const res = await ApiCustom.get(`/point-levels`, {
        params: { page: 1, limit: 1000, pointCategoryId: category.id },
      });
      const items = res.data?.data?.data || res.data?.data || [];
      const sorted =
        [...items].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id,
        ) || [];
      setLevels(sorted);
    } catch {
      toast.error("Không tải được mức điểm");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLevels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category?.id]);

  const openForm = (lv = null) => {
    if (!canEdit) return;
    if (lv) {
      if (isAuto) {
        const { op, value } = parseAutoLabel(lv.label);
        setForm({
          id: lv.id,
          label: "",
          op: op || "=",
          value: value === "" || isNaN(value) ? "" : value,
          points: lv.points ?? "",
          note: lv.note || "",
        });
      } else {
        setForm({
          id: lv.id,
          label: lv.label || "",
          op: "=",
          value: "",
          points: lv.points ?? "",
          note: lv.note || "",
        });
      }
    } else {
      setForm({
        id: null,
        label: "",
        op: "=",
        value: "",
        points: "",
        note: "",
      });
    }
    setFormOpen(true);
  };

  const saveLevel = async () => {
    if (!canEdit) return;

    const payloadBase = {
      pointCategoryId: category.id,
      note: form.note?.trim() || null,
    };

    if (form.points === "" || isNaN(Number(form.points))) {
      toast.error("Điểm phải là số hợp lệ");
      return;
    }
    payloadBase.points = Number(form.points);

    let payload = null;

    if (isAuto) {
      if (form.value === "" || isNaN(Number(form.value))) {
        toast.error("Nhập số mốc hợp lệ cho tiêu chí tự động");
        return;
      }
      const op = form.op || "=";
      const valStr = String(form.value).replace(",", ".");
      const label =
        (op && op !== "=" ? `${op} ` : "") + `${valStr} ${autoUnit}`;
      payload = { ...payloadBase, label };
    } else {
      if (!form.label?.trim()) {
        toast.error("Nhập nhãn mức");
        return;
      }
      payload = { ...payloadBase, label: form.label.trim() };
    }

    try {
      if (form.id) {
        await ApiCustom.put(`/point-levels/${form.id}`, payload, JSON_HEADERS);
        toast.success("Cập nhật mức điểm thành công");
      } else {
        await ApiCustom.post(`/point-levels`, payload, JSON_HEADERS);
        toast.success("Tạo mức điểm thành công");
      }
      setFormOpen(false);
      await loadLevels();
    } catch (e) {
      toast.error(e.response?.data?.message || "Lỗi khi lưu mức điểm");
    }
  };

  const requestDelete = (id) => setConfirm({ open: true, id });
  const doDelete = async () => {
    if (!canEdit) return;
    try {
      await ApiCustom.delete(`/point-levels/${confirm.id}`, JSON_HEADERS);
      toast.success("Đã xoá mức điểm");
      await loadLevels();
    } catch {
      toast.error("Xoá thất bại");
    } finally {
      setConfirm({ open: false, id: null });
    }
  };

  /* Nút lên/xuống: hoán đổi order giữa 2 bản ghi liền kề */
  const swapOrder = async (idxA, idxB) => {
    if (!canEdit) return;
    const a = levels[idxA];
    const b = levels[idxB];
    if (!a || !b) return;
    try {
      await Promise.all([
        ApiCustom.put(
          `/point-levels/${a.id}`,
          { pointCategoryId: category.id, order: b.order ?? 0 },
          JSON_HEADERS,
        ),
        ApiCustom.put(
          `/point-levels/${b.id}`,
          { pointCategoryId: category.id, order: a.order ?? 0 },
          JSON_HEADERS,
        ),
      ]);
      await loadLevels();
    } catch {
      toast.error("Đổi thứ tự thất bại");
    }
  };

  const headers = useMemo(() => {
    const base = [
      { key: "label", label: "Nhãn mức" },
      { key: "points", label: "Điểm" },
      { key: "note", label: "Ghi chú" },
      // KHÔNG hiển thị "Thứ tự"
    ];
    if (canEdit) base.push({ key: "setting", label: "Phương thức" });
    return base;
  }, [canEdit]);

  const settings = {
    renderSettings: (item, index) =>
      canEdit ? (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            title="Di chuyển lên"
            disabled={index === 0}
            onClick={() => swapOrder(index, index - 1)}
          >
            <BarsArrowUpIcon className="w-5 h-5" />
          </button>
          <button
            className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            title="Di chuyển xuống"
            disabled={index >= levels.length - 1}
            onClick={() => swapOrder(index, index + 1)}
          >
            <BarsArrowDownIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => openForm(item)}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
            title="Sửa mức điểm"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => requestDelete(item.id)}
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xoá mức điểm"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      ) : null,
  };

  return (
    <>
      <Modal
        isOpen={open}
        title={`Mức điểm · ${category?.name || ""}`}
        onClose={onClose}
        footer={
          hasPermission("POINT_SETTINGS") && (
            <div className="flex justify-end gap-2">
              <button className="btn btn-primary" onClick={() => openForm()}>
                <PlusCircleIcon className="w-5 h-5 mr-1" />
                Thêm mức
              </button>
            </div>
          )
        }
      >
        {loading ? (
          <div className="text-gray-500">Đang tải…</div>
        ) : (
          <TableComponent headers={headers} data={levels} settings={settings} />
        )}
      </Modal>

      <Modal
        isOpen={formOpen}
        title={form.id ? "Cập nhật mức điểm" : "Thêm mức điểm"}
        onClose={() => setFormOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="btn btn-ghost"
              onClick={() => setFormOpen(false)}
            >
              Huỷ
            </button>
            <button className="btn btn-primary" onClick={saveLevel}>
              Lưu
            </button>
          </div>
        }
      >
        {/* ==== LAYOUT CHUẨN THEO YÊU CẦU ==== */}
        {isAuto ? (
          // TỰ ĐỘNG: 1) Select toán tử + Input số (có suffix đơn vị) ở trên
          //          2) Điểm
          //          3) Ghi chú
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Điều kiện (2 cột) */}
            <div className="sm:col-span-2">
              <div className="label">
                <span className="label-text">Điều kiện</span>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="select select-bordered"
                  value={form.op}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, op: e.target.value }))
                  }
                >
                  <option value="=">=</option>
                  <option value=">">&gt;</option>
                  <option value=">=">&gt;=</option>
                  <option value="<">&lt;</option>
                  <option value="<=">&lt;=</option>
                </select>

                {/* Input có suffix đơn vị */}
                <div className="relative w-full">
                  <Input
                    type="number"
                    className="pr-24"
                    placeholder="Số"
                    value={form.value}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, value: e.target.value }))
                    }
                  />
                  <span className="pointer-events-none absolute right-3 inset-y-0 flex items-center text-sm text-gray-500">
                    {autoUnit}
                  </span>
                </div>
              </div>
            </div>

            {/* Điểm (1 cột) */}
            <div>
              <div className="label">
                <span className="label-text">Điểm</span>
              </div>
              <Input
                type="number"
                value={form.points}
                onChange={(e) =>
                  setForm((s) => ({ ...s, points: e.target.value }))
                }
              />
            </div>

            {/* Ghi chú full dòng */}
            <div className="sm:col-span-3">
              <div className="label">
                <span className="label-text">Ghi chú</span>
              </div>
              <Input
                value={form.note}
                onChange={(e) =>
                  setForm((s) => ({ ...s, note: e.target.value }))
                }
              />
            </div>
          </div>
        ) : (
          // THỦ CÔNG: 1) Nhãn mức + Điểm ở trên
          //           2) Ghi chú ở dưới
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="label">
                <span className="label-text">Nhãn mức</span>
              </div>
              <Input
                value={form.label}
                onChange={(e) =>
                  setForm((s) => ({ ...s, label: e.target.value }))
                }
              />
            </div>
            <div>
              <div className="label">
                <span className="label-text">Điểm</span>
              </div>
              <Input
                type="number"
                value={form.points}
                onChange={(e) =>
                  setForm((s) => ({ ...s, points: e.target.value }))
                }
              />
            </div>
            <div className="sm:col-span-2">
              <div className="label">
                <span className="label-text">Ghi chú</span>
              </div>
              <Input
                value={form.note}
                onChange={(e) =>
                  setForm((s) => ({ ...s, note: e.target.value }))
                }
              />
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={doDelete}
      />
    </>
  );
};

/* ======================== Trang chính (không hiển thị order, có nút lên/xuống) ======================== */
const PointManualSettings = () => {
  const canView = hasPermission("POINT_VIEW");
  const canEdit = hasPermission("POINT_SETTINGS");

  const [loading, setLoading] = useState(true);
  const [categoriesAll, setCategoriesAll] = useState([]);
  const [search, setSearch] = useState("");

  const [cateFormOpen, setCateFormOpen] = useState(false);
  const [cateForm, setCateForm] = useState({
    id: null,
    key: "",
    name: "",
  });

  const [confirm, setConfirm] = useState({ open: false, id: null });
  const [levelModal, setLevelModal] = useState({ open: false, category: null });

  const loadCategories = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const res = await ApiCustom.get(`/point-categories`, {
        params: { page: 1, limit: 1000, s: search },
      });
      const items = res.data?.data?.data || res.data?.data || [];
      const sorted =
        [...items].sort(
          (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id,
        ) || [];
      setCategoriesAll(sorted);
    } catch {
      toast.error("Không tải được tiêu chí");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const openCateForm = (cate = null) => {
    if (!canEdit) return;
    if (cate?.autoType) {
      toast.warn("Tiêu chí tự động không được đổi tên/mã");
      return;
    }
    if (cate) {
      setCateForm({
        id: cate.id,
        key: cate.key || "",
        name: cate.name || "",
      });
    } else {
      setCateForm({ id: null, key: "", name: "" });
    }
    setCateFormOpen(true);
  };

  const saveCategory = async () => {
    if (!canEdit) return;
    const payload = {
      key: cateForm.key?.trim(),
      name: cateForm.name?.trim(),
    };
    if (!payload.key || !payload.name) {
      toast.error("Vui lòng nhập Key và Tên");
      return;
    }
    try {
      if (cateForm.id) {
        await ApiCustom.put(
          `/point-categories/${cateForm.id}`,
          payload,
          JSON_HEADERS,
        );
        toast.success("Cập nhật tiêu chí thành công");
      } else {
        await ApiCustom.post(
          `/point-categories`,
          { ...payload, autoType: false, type: null },
          JSON_HEADERS,
        );
        toast.success("Tạo tiêu chí thành công");
      }
      setCateFormOpen(false);
      await loadCategories();
    } catch (e) {
      toast.error(e.response?.data?.message || "Lỗi khi lưu tiêu chí");
    }
  };

  const requestDelete = (item) => {
    if (item?.autoType) {
      toast.warn("Tiêu chí tự động không được xoá");
      return;
    }
    setConfirm({ open: true, id: item.id });
  };
  const doDelete = async () => {
    if (!canEdit) return;
    try {
      await ApiCustom.delete(`/point-categories/${confirm.id}`, JSON_HEADERS);
      toast.success("Đã xoá tiêu chí");
      await loadCategories();
    } catch {
      toast.error("Xoá thất bại");
    } finally {
      setConfirm({ open: false, id: null });
    }
  };

  const swapCateOrder = async (idxA, idxB) => {
    if (!canEdit) return;
    const a = categoriesAll[idxA];
    const b = categoriesAll[idxB];
    if (!a || !b) return;
    try {
      await Promise.all([
        ApiCustom.put(
          `/point-categories/${a.id}`,
          { order: b.order ?? 0 },
          JSON_HEADERS,
        ),
        ApiCustom.put(
          `/point-categories/${b.id}`,
          { order: a.order ?? 0 },
          JSON_HEADERS,
        ),
      ]);
      await loadCategories();
    } catch {
      toast.error("Đổi thứ tự thất bại");
    }
  };

  const headers = useMemo(() => {
    const base = [
      { key: "key", label: "Key" },
      { key: "name", label: "Tên tiêu chí" },
    ];
    if (canEdit) base.push({ key: "setting", label: "Phương thức" });
    return base;
  }, [canEdit]);

  const settings = {
    renderSettings: (item, index) =>
      canEdit ? (
        <div className="flex items-center gap-2">
          <button
            className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            title="Di chuyển lên"
            disabled={index === 0}
            onClick={() => swapCateOrder(index, index - 1)}
          >
            <BarsArrowUpIcon className="w-5 h-5" />
          </button>
          <button
            className="btn btn-outline btn-info btn-sm px-3 my-1 rounded"
            title="Di chuyển xuống"
            disabled={index >= categoriesAll.length - 1}
            onClick={() => swapCateOrder(index, index + 1)}
          >
            <BarsArrowDownIcon className="w-5 h-5" />
          </button>

          <button
            onClick={() => setLevelModal({ open: true, category: item })}
            className="btn btn-outline btn-warning btn-sm px-3 my-1 rounded"
            title="Chỉnh mức điểm"
          >
            <PencilSquareIcon className="w-5 h-5" />
          </button>

          <button
            onClick={() => openCateForm(item)}
            className="btn btn-outline btn-primary btn-sm px-3 my-1 rounded"
            title={
              item?.autoType
                ? "Tiêu chí tự động không cho đổi tên/mã"
                : "Sửa tên / mã"
            }
            disabled={!!item?.autoType}
          >
            <Cog6ToothIcon className="w-5 h-5" />
          </button>

          <button
            onClick={() => requestDelete(item)}
            className="btn btn-outline btn-error btn-sm px-3 my-1 rounded"
            title="Xoá tiêu chí"
            disabled={!!item?.autoType}
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        </div>
      ) : null,
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">Cài đặt tiêu chí điểm</h1>
        <div className="flex items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Tìm theo key / tên…"
            className="input input-bordered w-64"
          />
          {canEdit && (
            <button className="btn btn-success" onClick={() => openCateForm()}>
              <PlusCircleIcon className="w-5 h-5 mr-1" />
              Thêm tiêu chí
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500">Đang tải…</div>
      ) : (
        <TableComponent
          headers={headers}
          data={categoriesAll}
          settings={settings}
        />
      )}

      <Modal
        isOpen={cateFormOpen}
        title={cateForm.id ? "Cập nhật tiêu chí" : "Thêm tiêu chí"}
        onClose={() => setCateFormOpen(false)}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="btn btn-ghost"
              onClick={() => setCateFormOpen(false)}
            >
              Huỷ
            </button>
            <button className="btn btn-primary" onClick={saveCategory}>
              Lưu
            </button>
          </div>
        }
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <div className="label">
              <span className="label-text">Key</span>
            </div>
            <Input
              value={cateForm.key}
              onChange={(e) =>
                setCateForm((s) => ({ ...s, key: e.target.value }))
              }
            />
          </div>
          <div>
            <div className="label">
              <span className="label-text">Tên</span>
            </div>
            <Input
              value={cateForm.name}
              onChange={(e) =>
                setCateForm((s) => ({ ...s, name: e.target.value }))
              }
            />
          </div>
        </div>
      </Modal>

      <LevelEditorModal
        open={levelModal.open}
        category={levelModal.category}
        onClose={() => setLevelModal({ open: false, category: null })}
      />

      <ConfirmDeleteModal
        isOpen={confirm.open}
        onClose={() => setConfirm({ open: false, id: null })}
        onConfirm={doDelete}
      />
    </div>
  );
};

export default PointManualSettings;
